import type { Citation, GroundingBudget } from "@gencow/core";
import { DEFAULT_GROUNDING_BUDGET } from "@gencow/core";

const SNIPPET_MAX_CHARS = 400;
const MAX_VERIFY_LOOPS = 5;
const MAX_RESEARCH_QUERIES_PER_LOOP = 10;
const MAX_CITATIONS_PER_CLAIM = 10;
const MAX_CLAIMS_PER_ANSWER = 30;
const STOP_WORDS = new Set([
  "about",
  "after",
  "again",
  "allowed",
  "also",
  "common",
  "could",
  "does",
  "for",
  "from",
  "has",
  "have",
  "higher",
  "into",
  "than",
  "that",
  "the",
  "this",
  "what",
  "when",
  "where",
  "with",
]);

export function rowsFromExecute<T>(result: unknown): T[] {
  if (Array.isArray(result)) {
    return result as T[];
  }
  if (result && typeof result === "object" && Array.isArray((result as { rows?: unknown[] }).rows)) {
    return (result as { rows: T[] }).rows;
  }
  return [];
}

export function resolveGroundingBudget(input?: Partial<GroundingBudget>): GroundingBudget {
  return {
    maxVerifyLoops: positiveInteger(
      input?.maxVerifyLoops,
      DEFAULT_GROUNDING_BUDGET.maxVerifyLoops,
      MAX_VERIFY_LOOPS,
    ),
    maxResearchQueriesPerLoop: positiveInteger(
      input?.maxResearchQueriesPerLoop,
      DEFAULT_GROUNDING_BUDGET.maxResearchQueriesPerLoop,
      MAX_RESEARCH_QUERIES_PER_LOOP,
    ),
    maxCitationsPerClaim: positiveInteger(
      input?.maxCitationsPerClaim,
      DEFAULT_GROUNDING_BUDGET.maxCitationsPerClaim,
      MAX_CITATIONS_PER_CLAIM,
    ),
    maxClaimsPerAnswer: positiveInteger(
      input?.maxClaimsPerAnswer,
      DEFAULT_GROUNDING_BUDGET.maxClaimsPerAnswer,
      MAX_CLAIMS_PER_ANSWER,
    ),
  };
}

export function clampConfidence(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(1, value));
}

export function truncateSnippet(text: string, maxChars = SNIPPET_MAX_CHARS): string {
  const normalized = text.replace(/\s+/g, " ").trim();
  if (normalized.length <= maxChars) return normalized;
  return `${normalized.slice(0, Math.max(0, maxChars - 3)).trimEnd()}...`;
}

export function extractSignalTerms(text: string): string[] {
  const tokens = text.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
  return [...new Set(tokens.filter((token) => token.length > 1 && !STOP_WORDS.has(token)))];
}

export function citationKey(citation: Citation): string {
  return [citation.sourceId, citation.sectionId ?? "", citation.chunkIds.join(","), citation.snippet].join(
    "::",
  );
}

export function dedupeCitations(citations: Citation[]): Citation[] {
  const seen = new Set<string>();
  const result: Citation[] = [];
  for (const citation of citations) {
    const key = citationKey(citation);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(citation);
  }
  return result;
}

function positiveInteger(value: number | undefined, fallback: number, max: number): number {
  if (!Number.isInteger(value) || value === undefined || value <= 0) {
    return fallback;
  }
  return Math.min(value, max);
}
